import { useCallback, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../../lib/supabase";
import { T_LIGHT } from "../tokens";

const LS_WIDTH_KEY = "mannaChatRailWidth";
const LS_COLLAPSED_KEY = "mannaChatRailCollapsed";
const MIN_W = T_LIGHT.rail_w_min;
const MAX_W = T_LIGHT.rail_w_max;
const DEFAULT_W = T_LIGHT.rail_w_default;
const DEBOUNCE_MS = 1000;

const prefsKey = ["user_ui_preferences"] as const;

interface UiPrefs {
  chat_rail_collapsed: boolean;
  chat_rail_width_px: number;
  run_drawer_width_px: number;
}

function readLocalWidth(): number {
  if (typeof window === "undefined") return DEFAULT_W;
  const raw = window.localStorage.getItem(LS_WIDTH_KEY);
  const n = raw ? Number.parseInt(raw, 10) : NaN;
  if (Number.isFinite(n) && n >= MIN_W && n <= MAX_W) return n;
  return DEFAULT_W;
}

function readLocalCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(LS_COLLAPSED_KEY) === "1";
}

function clamp(n: number): number {
  if (n < MIN_W) return MIN_W;
  if (n > MAX_W) return MAX_W;
  return n;
}

function useUiPreferencesQuery() {
  return useQuery({
    queryKey: prefsKey,
    queryFn: async (): Promise<UiPrefs | null> => {
      const { data, error } = await supabase
        .from("user_ui_preferences_view")
        .select("chat_rail_collapsed, chat_rail_width_px, run_drawer_width_px")
        .maybeSingle();
      if (error) throw error;
      return data as UiPrefs | null;
    },
    staleTime: 5 * 60_000,
  });
}

function useUpsertUiPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<UiPrefs>) => {
      const { error } = await supabase.rpc("upsert_user_ui_preferences", {
        payload: payload as never,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: prefsKey }),
  });
}

export function useChatRailWidth() {
  const { data } = useUiPreferencesQuery();
  const upsert = useUpsertUiPrefs();
  const [overrideWidth, setOverrideWidth] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Render-time precedence: local user override → server pref → cache → default.
  const widthPx =
    overrideWidth ??
    (data?.chat_rail_width_px != null ? clamp(data.chat_rail_width_px) : readLocalWidth());

  const setWidthPx = useCallback(
    (next: number) => {
      const w = clamp(Math.round(next));
      setOverrideWidth(w);
      window.localStorage.setItem(LS_WIDTH_KEY, String(w));
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        upsert.mutate({ chat_rail_width_px: w });
      }, DEBOUNCE_MS);
    },
    [upsert],
  );

  return { widthPx, setWidthPx, isResizing, setIsResizing, MIN_W, MAX_W, DEFAULT_W };
}

export function useChatRailCollapsed() {
  const { data } = useUiPreferencesQuery();
  const upsert = useUpsertUiPrefs();
  const [overrideCollapsed, setOverrideCollapsed] = useState<boolean | null>(null);

  const collapsed =
    overrideCollapsed ??
    (typeof data?.chat_rail_collapsed === "boolean"
      ? data.chat_rail_collapsed
      : readLocalCollapsed());

  const setCollapsed = useCallback(
    (next: boolean) => {
      setOverrideCollapsed(next);
      window.localStorage.setItem(LS_COLLAPSED_KEY, next ? "1" : "0");
      upsert.mutate({ chat_rail_collapsed: next });
    },
    [upsert],
  );

  return { collapsed, setCollapsed, toggle: () => setCollapsed(!collapsed) };
}
